import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { AuthModal } from "./AuthModal";

export function UserMenu() {
  const navigate = useNavigate();
  const { session, isAuthenticated, isLoading, signOut } = useAuth();
  const { activeOrganization, organizations, isViewOnly, setActiveOrganization } =
    useOrganization();
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate("/");
  };

  if (isLoading) {
    return <div className="h-10 w-24 animate-pulse rounded-xl bg-slate-100" />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Sign in
        </button>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  const initials = (session?.user.name || session?.user.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // SFWL-020 demo fix: members of a child org (not admin/owner) had no
  // indication anywhere of which org they were in. activeOrganization already
  // carries activeMemberRole (see useOrganization's isAdmin fallback) — reuse
  // it here rather than issuing a second fetch.
  const activeOrgData = activeOrganization as Record<string, unknown> | null;
  const activeOrgRole = activeOrgData?.activeMemberRole as string | undefined;

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen((o) => !o)}
        // Chip sits directly on the Header's surface, which recolors for a
        // themed tenant (SFWL-002) via --tenant-background/--tenant-foreground
        // -- match that here (border/text) with the same light-mode fallback
        // so bare/default localhost is unaffected. The chevron svg below
        // uses stroke="currentColor" so it inherits this text color too.
        className="flex items-center gap-2 rounded-xl border border-[color:var(--tenant-background,#e2e8f0)] px-3 py-2 text-sm font-medium text-[color:var(--tenant-foreground,#334155)] transition hover:bg-slate-50"
      >
        {session?.user.image ? (
          <img
            src={session.user.image}
            alt=""
            width={28}
            height={28}
            className="h-7 w-7 rounded-full"
          />
        ) : (
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
            {initials}
          </div>
        )}
        <span className="hidden sm:inline">
          {session?.user.name || session?.user.email}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={`transition ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-slate-200 bg-white py-2 shadow-lg">
          <div className="border-b border-slate-100 px-4 py-2">
            <p className="text-sm font-medium text-slate-900">{session?.user.name}</p>
            <p className="text-xs text-slate-500">{session?.user.email}</p>
            {activeOrgData && (
              <p className="mt-1 truncate text-xs text-slate-400">
                {activeOrgData.name as string}
                <span className="mx-1 text-slate-300">&middot;</span>
                <span className="capitalize">
                  {isViewOnly ? "View only" : activeOrgRole || "member"}
                </span>
              </p>
            )}
          </div>

          {/* Organization switcher */}
          {organizations.length > 0 && (
            <div className="border-b border-slate-100 px-4 py-2">
              <button
                onClick={() => setShowOrgSwitcher((v) => !v)}
                className="flex w-full items-center justify-between text-xs font-medium text-slate-500"
              >
                <span className="truncate">
                  {activeOrganization
                    ? ((activeOrganization as Record<string, unknown>).name as string)
                    : "Select organization"}
                </span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  className={`ml-1 shrink-0 transition ${showOrgSwitcher ? "rotate-180" : ""}`}
                >
                  <path
                    d="M2.5 3.75L5 6.25L7.5 3.75"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </button>
              {showOrgSwitcher && (
                <div className="mt-1 max-h-32 space-y-0.5 overflow-y-auto">
                  {organizations.map((org: Record<string, unknown>) => (
                    <button
                      key={org.id as string}
                      onClick={() => {
                        setActiveOrganization(org.id as string);
                        setShowOrgSwitcher(false);
                      }}
                      className={`w-full rounded-lg px-2 py-1.5 text-left text-xs transition ${
                        activeOrganization &&
                        (activeOrganization as Record<string, unknown>).id === org.id
                          ? "bg-slate-100 font-medium text-slate-900"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {org.name as string}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <Link
            to="/settings"
            onClick={() => setIsOpen(false)}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M6.5 1.5h3l.4 1.6.3.1 1.4-.8 2.1 2.1-.8 1.4.1.3 1.6.4v3l-1.6.4-.1.3.8 1.4-2.1 2.1-1.4-.8-.3.1-.4 1.6h-3l-.4-1.6-.3-.1-1.4.8-2.1-2.1.8-1.4-.1-.3L1.5 9.5v-3l1.6-.4.1-.3-.8-1.4 2.1-2.1 1.4.8.3-.1.4-1.6z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
                fill="none"
              />
              <circle
                cx="8"
                cy="8"
                r="2"
                stroke="currentColor"
                strokeWidth="1.2"
                fill="none"
              />
            </svg>
            Settings
          </Link>

          <button
            onClick={handleSignOut}
            className="w-full px-4 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
